import { useCallback } from "react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { QuickJump } from "@graylog/server-api";
import i18n from "i18n";
import { defaultOnError } from "util/conditional/onError";
import { useGetEntityRoute } from "routing/hooks/useShowRouteForEntity";
import useNavItems from "components/quick-jump/hooks/useNavItems";
import { PAGE_WEIGHT, BASE_SCORE } from "components/quick-jump/Constants";
const compareSearchItems = (result1, result2) => {
  var _a, _b;
  const scoreDifference = result2.score - result1.score;
  if (scoreDifference === 0) {
    return Number((_a = result2.last_opened) != null ? _a : false) - Number((_b = result1.last_opened) != null ? _b : false);
  }
  return scoreDifference;
};
const normalize = (s) => s.toLocaleLowerCase().trim();
const scoreItem = (item, query) => {
  const normalizedTitle = normalize(item.title);
  const normalizedQuery = normalize(query);
  if (normalizedTitle === normalizedQuery) {
    return BASE_SCORE;
  }
  if (normalizedTitle.startsWith(query)) {
    return BASE_SCORE - 1;
  }
  if (normalizedTitle.includes(normalizedQuery)) {
    return BASE_SCORE - 2;
  }
  return 0;
};
const scoreResults = (items, query, weight = 1) => items.flatMap((item) => {
  const score = scoreItem(item, query);
  if (score === 0) {
    return [];
  }
  return [{ ...item, score: score * weight }];
});
const useEntityResultMapper = () => {
  const getEntityRoute = useGetEntityRoute();
  return useCallback(
    (item) => ({
      ...item,
      key: item.id,
      link: getEntityRoute(item.id, item.type)
    }),
    [getEntityRoute]
  );
};
const useQuickJumpSearch = (searchQuery) => {
  const mapEntityResult = useEntityResultMapper();
  const navItems = useNavItems();
  const { data: searchResultItems, isLoading } = useQuery({
    queryKey: ["quick-jump", searchQuery],
    queryFn: () => defaultOnError(
      (async () => {
        const entities = await QuickJump.search({ query: searchQuery, limit: 100 });
        const scoredNavItems = scoreResults(navItems, searchQuery, PAGE_WEIGHT);
        const entityResultItems = entities.results.map(mapEntityResult);
        return (searchQuery.trim() !== "" ? [...entityResultItems, ...scoredNavItems] : [...entityResultItems]).sort(
          compareSearchItems
        );
      })(),
      i18n.t("notifications.quick_jump_failed", { ns: "common" }),
      i18n.t("notifications.quick_jump_failed_title", { ns: "common" })
    ),
    placeholderData: keepPreviousData,
    refetchOnMount: "always"
  });
  return {
    searchResults: searchResultItems != null ? searchResultItems : [],
    isLoading
  };
};
export default useQuickJumpSearch;
